/**
 * Edit one person — side panel (guide §9). PATCH /api/v1/users/{id} with If-Match, sending only what changed.
 * Disabling deletes all their sessions in the same transaction, so access ends immediately (ADR §6.1).
 */
import { useEffect, useState } from 'react';
import { ROLE_LABEL, type UpdateUserBody, type User } from '@amplispot/contracts';
import { mayManagePassword } from '@amplispot/domain';
import { ApiFailure } from '../../api';
import { useSetCapability, useUpdateUser } from '../../lib/data';
import { useSession } from '../../lib/session';
import { canActAs } from '../../lib/actas';
import { Avatar, Drawer, Field, Modal, Switch, errorText, useToast } from '../../ui/kit';
import { usePasswordRemove, usePasswordReset } from '../../lib/password';
import { ActAsActions } from '../ActAsModal';

/** The editable fields, as the form shows them. */
type Form = { name: string; initials: string; phone: string; joined: string; cap: string };
const formOf = (u: User): Form => ({
  name: u.name,
  initials: u.initials,
  phone: u.phone ?? '',
  joined: u.joined_on ?? '',
  cap: String(u.capacity_adjustment_pct),
});
const FIELD_WORDS: Record<keyof Form, string> = {
  name: 'name',
  initials: 'initials',
  phone: 'phone',
  joined: 'joining date',
  cap: 'load adjustment',
};

export function UserDrawer({ user, onClose }: { user: User; onClose: () => void }) {
  const { me } = useSession();
  const update = useUpdateUser();
  const toast = useToast();
  const [name, setName] = useState(user.name);
  const [initials, setInitials] = useState(user.initials);
  const [phone, setPhone] = useState(user.phone ?? '');
  const [joined, setJoined] = useState(user.joined_on ?? '');
  const [cap, setCap] = useState(String(user.capacity_adjustment_pct));
  // What the form was seeded from, and the version that goes out as If-Match (ADR §11.1). The form is diffed
  // against THIS, never against the live list row, so a refetch can never turn someone else's edit into "my change".
  const [base, setBase] = useState<{ form: Form; version: number }>(() => ({ form: formOf(user), version: user.version }));
  const [theirs, setTheirs] = useState<string | null>(null);

  // A newer copy of this person arrived (after a 412, or any refetch): take every field the other save changed,
  // keep my own edits to the fields it did not touch, say what they changed, and only then allow Save with the new version.
  useEffect(() => {
    if (user.version === base.version) return;
    const fresh = formOf(user);
    const mine: Form = { name, initials, phone, joined, cap };
    const changed = (Object.keys(fresh) as (keyof Form)[]).filter((k) => fresh[k] !== base.form[k]);
    const next = { ...mine };
    for (const k of changed) next[k] = fresh[k];
    setName(next.name);
    setInitials(next.initials);
    setPhone(next.phone);
    setJoined(next.joined);
    setCap(next.cap);
    setBase({ form: fresh, version: user.version });
    setTheirs(
      changed.length
        ? `Someone else saved this person just now and changed ${changed.map((k) => `the ${FIELD_WORDS[k]} to “${fresh[k] || 'empty'}”`).join(', ')}. Their change is in the form below. Your other edits are kept: check them, then save again.`
        : 'Someone else saved this person just now. Nothing you are editing was changed: save again to keep your edits.',
    );
    setFormErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs only when a newer version of the person arrives
  }, [user.version]);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [formErr, setFormErr] = useState<string | null>(null);
  const isSelf = user.id === me.user.id;

  const send = (body: UpdateUserBody, done: string) => {
    setFormErr(null);
    update.mutate(
      { id: user.id, version: body.status ? user.version : base.version, body },
      {
        onSuccess: () => {
          toast(done);
          onClose();
        },
        onError: (e) => {
          if (e instanceof ApiFailure) setErrs(e.fieldErrors);
          setFormErr(
            e instanceof ApiFailure && e.status === 412
              ? 'Someone else changed this person a moment ago. Loading their latest details…'
              : errorText(e),
          );
        },
      },
    );
  };

  const save = () => {
    const body: UpdateUserBody = {};
    const local: Record<string, string> = {};
    const b = base.form;
    if (name.trim() !== b.name) body.name = name.trim();
    if (initials.trim().toUpperCase() !== b.initials) body.initials = initials.trim();
    if (phone.trim() !== b.phone) body.phone = phone.trim() ? phone.trim() : null;
    if (joined !== b.joined) body.joined_on = joined || null;
    const capN = Number(cap);
    if (!Number.isInteger(capN) || capN < -100 || capN > 100) local.capacity_adjustment_pct = 'Use a whole number from -100 to 100';
    else if (String(capN) !== b.cap) body.capacity_adjustment_pct = capN;
    if (!name.trim()) local.name = 'Enter a name';
    setErrs(local);
    if (Object.keys(local).length) return;
    setTheirs(null);
    if (!Object.keys(body).length) {
      onClose();
      return;
    }
    send(body, `${name.trim()} saved`);
  };

  const toggleStatus = () => {
    if (user.status === 'active' && !confirmDisable) {
      setConfirmDisable(true);
      return;
    }
    send(
      { status: user.status === 'active' ? 'disabled' : 'active' },
      user.status === 'active' ? `${user.name} is disabled. Their access ended now.` : `${user.name} can sign in again`,
    );
  };

  return (
    <Drawer
      title="Person"
      onClose={onClose}
      footer={
        <>
          {!isSelf ? (
            <button className={`btn ${user.status === 'active' ? 'bad' : 'good'}`} onClick={toggleStatus} disabled={update.isPending}>
              {user.status === 'active' ? (confirmDisable ? 'Yes, disable now' : 'Disable access') : 'Enable access'}
            </button>
          ) : null}
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <div className="split" style={{ gap: 14, marginBottom: 18 }}>
        <Avatar user={user} size={44} />
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: 19 }}>{user.name}</h2>
          <div className="sub">
            {user.email} · {ROLE_LABEL[user.role]}
            {user.status === 'disabled' ? ' · Disabled' : ''}
          </div>
        </div>
      </div>
      {formErr ? <div className="note bad">{formErr}</div> : null}
      {theirs ? (
        <div className="note warn" role="status">
          {theirs}
        </div>
      ) : null}
      {confirmDisable ? (
        <div className="note warn">
          Disabling signs {user.name} out everywhere straight away. Their records and history stay.{user.client_count ? ` They are SPOC for ${user.client_count} client${user.client_count === 1 ? '' : 's'}; give those clients a new SPOC in Settings → SPOCs.` : ''}
        </div>
      ) : null}
      <div className="cols c2">
        <Field label="Name" error={errs.name}>
          <input className="in" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Initials" error={errs.initials}>
          <input className="in" maxLength={3} value={initials} onChange={(e) => setInitials(e.target.value.toUpperCase())} />
        </Field>
      </div>
      <Field label="Phone" error={errs.phone} hint="For WhatsApp messages. Leave empty to remove it.">
        <input className="in" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+919820012345" />
      </Field>
      <div className="cols c2">
        <Field label="Joined on" error={errs.joined_on}>
          <input className="in" type="date" value={joined} onChange={(e) => setJoined(e.target.value)} />
        </Field>
        <Field label="Load adjustment (%)" error={errs.capacity_adjustment_pct} hint="0 is the standard load. -20 means 20% lighter.">
          <input className="in" type="number" min={-100} max={100} step={1} value={cap} onChange={(e) => setCap(e.target.value)} />
        </Field>
      </div>
      <dl className="kv" style={{ marginTop: 6 }}>
        <dt>SPOC for</dt>
        <dd>
          {user.client_count} client{user.client_count === 1 ? '' : 's'}
        </dd>
        <dt>Role</dt>
        <dd>{ROLE_LABEL[user.role]} (change it in the list)</dd>
      </dl>
      <PasswordSection user={user} />
      <HrAdminSection user={user} />
      <ActAsSection user={user} />
    </Drawer>
  );
}

/**
 * Operator decisions 11–12: 'HR admin (holiday calendar)' = the `holidays.manage` grant (ADR-005's hr.admin), on top
 * of the role. The switch is locked exactly where PUT/DELETE /users/{id}/capabilities/holidays.manage would refuse:
 * the hr role holds it by role, never your own row, a founder's only by a founder, never while acting or viewing as
 * someone, and a disabled person can lose it but not be given it (409).
 */
function HrAdminSection({ user }: { user: User }) {
  const { me, can } = useSession();
  const setCap = useSetCapability();
  const toast = useToast();
  if (!can('users.write') && !me.acting_as) return null;
  const byRole = user.role === 'hr';
  const granted = user.granted_capabilities.includes('holidays.manage');
  const on = byRole || granted;
  const first = user.name.split(' ')[0];
  const locked = byRole
    ? `${first} has it as HR. It comes with the role.`
    : me.acting_as
      ? `You are ${me.acting_as.mode === 'view' ? 'viewing' : 'acting'} as ${me.acting_as.as.name}. Stop first: this is changed only in your own name.`
      : user.id === me.user.id
        ? 'You cannot change your own access. Ask another manager.'
        : user.role === 'founder' && me.user.role !== 'founder'
          ? 'Only a founder can change what a founder holds.'
          : !granted && user.status === 'disabled'
            ? `${first} is disabled. Enable access first, then give it.`
            : null;
  const busy = setCap.isPending;
  const flip = () =>
    setCap.mutate(
      { id: user.id, capability: 'holidays.manage', on: !granted },
      {
        onSuccess: () => toast(granted ? `${first} no longer manages the holiday calendar` : `${first} can now manage the holiday calendar`),
        onError: (e) => toast(errorText(e)),
      },
    );
  return (
    <div className="drawer-sec">
      <h4>Extra access</h4>
      <div className="split" style={{ gap: 14, alignItems: 'flex-start' }}>
        <div className="grow" style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>HR admin (holiday calendar)</div>
          <div className="sub" style={{ fontSize: 12.5 }}>
            Draft and publish the holiday calendar, and set day types. It shows nothing about clients, tasks, timesheets, usage or revenue.
          </div>
        </div>
        <Switch on={on} disabled={!!locked || busy} onClick={flip} label={`HR admin (holiday calendar) for ${user.name}`} />
      </div>
      {locked ? <div className="sub" style={{ fontSize: 12.5 }}>{locked}</div> : null}
    </div>
  );
}

/** "See what they see" — only for people on your acting ladder (ADR-006); nothing renders otherwise. */
function ActAsSection({ user }: { user: User }) {
  const { me, can } = useSession();
  if (!canActAs(me, can, user)) return null;
  return (
    <div className="actas-sec">
      <h4>See what {user.name} sees</h4>
      <p className="sub">View as them (read-only), or act as them to do their work, for up to 30 minutes. You give a reason; it is kept with everything you do.</p>
      <div className="split" style={{ gap: 8, flexWrap: 'wrap' }}>
        <ActAsActions user={user} />
      </div>
    </div>
  );
}

const PASSWORD_STATE: Record<User['sign_in_password'], string> = {
  none: 'No password. Signs in with Google only.',
  set: 'Has a password. Can sign in with Google or email and password.',
  temporary: 'Has a temporary password. Chooses their own at their next sign-in.',
};

/**
 * Email + password sign-in (operator decision 17 Oct 2026). Managers with users.write only; nothing renders for
 * anyone else. POST /users/{id}/password-reset returns a temporary password ONCE (it is kept only as a hash), shown in
 * a pop-up with a copy button; DELETE /users/{id}/password leaves Google only. Both sign the person out everywhere.
 * Locked exactly where the server would refuse (403/409): your own row, anyone you may not act as unless you are a
 * founder (mayManagePassword: never a peer AD's, HR's or a founder's — sf-b226aae7), while acting or viewing as
 * someone, and (reset only) a disabled person.
 */
export function PasswordSection({ user }: { user: User }) {
  const { me, can } = useSession();
  const reset = usePasswordReset();
  const remove = usePasswordRemove();
  const toast = useToast();
  const [confirm, setConfirm] = useState<'reset' | 'remove' | null>(null);
  const [temp, setTemp] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (!can('users.write')) return null;
  const first = user.name.split(' ')[0];
  const locked = me.acting_as
    ? `You are ${me.acting_as.mode === 'view' ? 'viewing' : 'acting'} as ${me.acting_as.as.name}. Stop first: passwords are changed only in your own name.`
    : user.id === me.user.id
      ? 'You cannot reset your own password here. Ask another manager.'
      : !mayManagePassword(me.user.role, user.role)
        ? user.role === 'founder'
          ? 'Only a founder can reset a founder’s password.'
          : `Only a founder can reset a password for the ${ROLE_LABEL[user.role]} role.`
        : null;
  const resetLocked = !locked && user.status === 'disabled' ? `${first} is disabled. Enable access first, then reset the password.` : null;
  const busy = reset.isPending || remove.isPending;

  const doReset = () => {
    setErr(null);
    reset.mutate(user.id, {
      onSuccess: (r) => {
        setConfirm(null);
        setTemp(r.temporary_password);
      },
      onError: (e) => setErr(errorText(e)),
    });
  };
  const doRemove = () => {
    setErr(null);
    remove.mutate(user.id, {
      onSuccess: () => {
        setConfirm(null);
        toast(`${first}’s password is removed. They sign in with Google only.`);
      },
      onError: (e) => setErr(errorText(e)),
    });
  };

  return (
    <div className="drawer-sec">
      <h4>Password</h4>
      <div className="sub" style={{ fontSize: 12.5, marginBottom: 10 }} data-testid="password-state">
        {PASSWORD_STATE[user.sign_in_password]}
      </div>
      {err ? <div className="note bad">{err}</div> : null}
      {confirm === 'reset' ? (
        <div className="note warn">
          {first} gets a new temporary password, shown to you once. {user.sign_in_password === 'none' ? '' : 'Their current password stops working, and '}
          {user.sign_in_password === 'none' ? 'They are' : 'they are'} signed out everywhere now. They choose their own password at their next sign-in.
        </div>
      ) : null}
      {confirm === 'remove' ? (
        <div className="note warn">
          {first} can then sign in with Google only, and is signed out everywhere now.
        </div>
      ) : null}
      <div className="split" style={{ gap: 8, flexWrap: 'wrap' }}>
        {confirm ? (
          <>
            <button className={`btn sm ${confirm === 'remove' ? 'bad' : 'pri'}`} onClick={confirm === 'reset' ? doReset : doRemove} disabled={busy}>
              {busy ? 'Working…' : confirm === 'reset' ? 'Yes, reset password' : 'Yes, remove password'}
            </button>
            <button className="btn sm" onClick={() => setConfirm(null)} disabled={busy}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button className="btn sm" onClick={() => setConfirm('reset')} disabled={!!locked || !!resetLocked}>
              {user.sign_in_password === 'none' ? 'Give a password' : 'Reset password'}
            </button>
            {user.sign_in_password !== 'none' ? (
              <button className="btn sm bad" onClick={() => setConfirm('remove')} disabled={!!locked}>
                Remove password
              </button>
            ) : null}
          </>
        )}
      </div>
      {locked || resetLocked ? <div className="sub" style={{ fontSize: 12.5, marginTop: 8 }}>{locked ?? resetLocked}</div> : null}
      {temp ? <TemporaryPasswordModal name={user.name} email={user.email} password={temp} onClose={() => setTemp(null)} /> : null}
    </div>
  );
}

/** The temporary password, once. Closing the pop-up forgets it: it cannot be shown again, only reset again. */
function TemporaryPasswordModal({ name, email, password, onClose }: { name: string; email: string; password: string; onClose: () => void }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      toast('Temporary password copied');
    } catch {
      toast('Select the password and copy it');
    }
  };
  return (
    <Modal
      title={`Temporary password for ${name}`}
      sub={`They sign in with ${email} and this password, then choose their own.`}
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Done
          </button>
          <button className="btn pri" onClick={copy}>
            {copied ? 'Copied' : 'Copy password'}
          </button>
        </>
      }
    >
      <Field label="Temporary password">
        <input
          className="in"
          readOnly
          value={password}
          onFocus={(e) => e.currentTarget.select()}
          style={{ fontFamily: 'var(--mono)', letterSpacing: '.04em' }}
          data-testid="temporary-password"
        />
      </Field>
      <div className="note warn" role="note">
        This is the only time it is shown. Copy it now and give it to {name.split(' ')[0]} directly, not in a shared channel. If it is lost, reset the
        password again.
      </div>
    </Modal>
  );
}
